import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import media from 'styled-media-query';
import { breakpointsObject as bp } from '@firsttable/web-theme';
import {
  color,
  space,
  display,
  width,
  height,
  ColorProps,
  SpaceProps,
  HeightProps,
  DisplayProps,
} from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import React from 'react';

type LinkStyles = {
  underline: boolean | string;
  expandClickableArea: boolean;
};

type LinkProps = {
  to?: any;
  href?: any;
  underline?: any;
  onClick?: (...args) => void;
  external?: boolean;
  target?: string;
  tabIndex?: string;
  active?: boolean;
  expandClickableArea?: boolean;
  NavComponent: any;
  children: any;
  as?: any;
  itemProp?: any;
  download?: boolean;
} & ColorProps &
  SpaceProps &
  HeightProps &
  DisplayProps;

export const styles = css<LinkStyles>`
  ${({ underline }) => underline === 'true' && 'border-bottom: 1px solid'};
  text-decoration: none;
  ${({ underline }) =>
    underline === 'true' &&
    css`
      &:hover {
        border-bottom-color: transparent;
      }
    `};
  cursor: pointer;
  ${space};
  ${color};
  ${display};
  ${width};
  ${height};
  ${({ expandClickableArea }) =>
    expandClickableArea &&
    css`
      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        z-index: 1;
      }
    `};
  &:active {
    color: ${themeGet('colors.gold.900')};
  }
  &:focus:not(.active) {
    text-decoration: underline;
    text-underline-offset: 0.2em;
  }
  ${media.lessThan(bp.m)`
    text-underline-offset: 0.3em;
    text-decoration-thickness: 1px;
    &:active {
      text-decoration: underline !important;
    }
    &:hover {
      text-decoration: none;
    }
  `}
`;

const StyledLink = styled.a`
  ${styles};
  [active]
`;

const linkDefaultOnClick = () => {};
const Link = ({
  to = null,
  href = null,
  underline = false,
  onClick = linkDefaultOnClick,
  external = true,
  active,
  expandClickableArea = false,
  NavComponent = null,
  ...rest
}: LinkProps) => {
  const props = {
    underline: underline.toString(),
    expandClickableArea,
    ...rest,
  };

  if ((href && NavComponent) || (process.env.NEXT_PUBLIC_SITE_ID && to)) {
    return (
      <NavComponent href={href || to} passHref {...props}>
        <StyledLink {...props} className={active ? 'active' : ''} />
      </NavComponent>
    );
  }
  if (to) {
    const styledNavLinkProps = {
      to,
      getProps: ({ isCurrent }: { isCurrent: any }) => ({
        active: isCurrent ? isCurrent.toString() : null,
      }),
      ...props,
    };
    return <NavComponent onClick={onClick} {...styledNavLinkProps} />;
  }
  if (href) {
    return (
      <StyledLink
        target={external ? '_blank' : '_self'}
        // @ts-ignore
        rel={external ? 'noopener noreferrer nofollow' : null}
        href={href}
        as="a"
        active={active ? 'true' : null}
        {...props}
      />
    );
  }
  return <StyledLink role="button" as="span" onClick={onClick} {...props} />;
};

Link.propTypes = {
  to: PropTypes.string,
  href: PropTypes.string,
  underline: PropTypes.bool,
  onClick: PropTypes.func,
  external: PropTypes.bool,
  expandClickableArea: PropTypes.bool,
  NavComponent: PropTypes.any,
  scroll: PropTypes.bool,
  // ...display.propTypes, ...space.propTypes, ...color.propTypes,
};

export default Link;
